import React from 'react';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { PesCcMain } from '@pes-ui/components-react';
import { BaseRouteProps } from '@pes-ui/utils/lib/types/route-props';

import styles from './index.module.scss';
import OptionsCard from '../OptionsCard';
import MobileCompareResults from './MobileCompareResults';
import MobileProductsColumn from '../MobileProductsColumn';
import { isCategorySectionAvailable, isHostnameContainsCC } from '../../utils';

import { CircularProductTypes } from '@pes-ui/utils/lib/constants';
import { ProductsApi } from '@pes-ui/utils/lib/types/api/products-api';
import { ProductsParametersApi } from '@pes-ui/utils/lib/types/api/products-parameters-api';
import { ProductInfoApi } from '@pes-ui/utils/lib/types/api/products-info-api';
import { CommerceConnectorApi } from '@pes-ui/utils/lib/types/api/commerce-connector-api';
import { MappedProduct } from '../../utils/mappers';
import { TProductsRatings } from '@pes-ui/components/dist/collection/globals/types';
import { ProductAvailabilityApi } from '@pes-ui/utils/lib/types/api/product-availability-api';
import { ApcCtaApi } from '@pes-ui/utils/lib/types/api/apc-cta-api';
import { getAPCDistributor } from '@pes-ui/components/dist/collection/components/commerce-connector/utils';
import { ProductsDistributorsInfo } from '@pes-ui/components/dist/collection/components/commerce-connector/types';
import { ComparatorRuntimeInfo } from '@pes-ui/utils/lib/types/comparator';


type Props = BaseRouteProps & {
    products: MappedProduct[];
    productsUIAAS: ProductsApi.Products;
    productsUIAASinfo: ProductInfoApi.GetProductsInfoResponse['productAdditionalInfos'];
    labels: ProductsApi.Labels;
    downloadButtonLabels: ProductInfoApi.DownloadButtonLabels;
    commerceConnector: CommerceConnectorApi.GetCommerceConnectorResponse;
    myProducts: string[];
    categories: ProductsParametersApi.Sections;
    keyCategories: ProductsParametersApi.Sections;
    onRemove: (arg) => void;
    onUpdateMyProductsList: () => void;
    toggleSectionHandler: (currentStateKey: string) => void;
    toggleSectionState: Record<string, boolean>;
    showDevPrioritiesInfo: boolean;
    handleHideSimilarChange: () => void;
    hideSimilarFeatures: boolean;
    highlightDifferencesChange: () => void;
    highlightDifferences: boolean;
    hideSimilaritiesCalculated?: boolean;
    productsRatings?:TProductsRatings;
    bazaarVoiceEnabled?: boolean;
    isAPCSite?: boolean;
    apcCta?: ApcCtaApi.ApcCtaResponse;
    productAvailabilityInfo?:ProductAvailabilityApi.ProductAvailabilityResponse;
    runtimeInfo?: ComparatorRuntimeInfo;
    linkClickHandlerCallback?: (link: string) => void;
};

type State = {
    left: number;
    right: number;
    currentKey: any;
    index: number;
    isStuck: boolean;
    ccProductIdToShow: string;
    ccBuyOnlineAvailability: Record<string, boolean>;
    ccPricesMap: Record<string, string>;
};

class MobileProducts extends React.Component<Props, State> {
    static defaultProps = {
        showDevPrioritiesInfo: false,
        categories: [],
        keyCategories: [],
        products: [],
        updateProductRunTimeValue: () => ({}),
    }
    private intersectionObserver: IntersectionObserver;
    private minifiedProductsContainer: React.RefObject<HTMLDivElement>;
    private stickyContainer: React.RefObject<HTMLDivElement>;

    constructor(props){
        super(props);
        this.state = {
            left: 0,
            right: 1,
            currentKey: null,
            ccPricesMap: null,
            index: null,
            isStuck: false,
            ccProductIdToShow: null,
            ccBuyOnlineAvailability: {},
        };
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
            this.setState({ isStuck: entry.boundingClientRect.y < entry.rootBounds.y});
        }, {threshold: [1]});
        this.minifiedProductsContainer = React.createRef<HTMLDivElement>();
        this.stickyContainer = React.createRef<HTMLDivElement>();
    }

    componentDidMount() {
        this.intersectionObserver.observe(this.minifiedProductsContainer.current);
    }

    componentDidUpdate() {
        if (!this.stickyContainer.current.style.top) {
            const minifiedProductsHeight = this.minifiedProductsContainer.current.getBoundingClientRect().height;
            this.stickyContainer.current.style.marginTop = `-${minifiedProductsHeight}px`;
        }
    }

    componentWillUnmount() {
        this.intersectionObserver.unobserve(this.minifiedProductsContainer.current);
    }

    preloadImage = product => <link key={product.productId} rel="preload" href={product.image} as="image" />;

    getKeyByValue = (object, value) => Object.keys(object).find(key => object[key] === value);

    nextProductActivate = (currentKey) => () => {
        const { products } = this.props;
        const startFrom = this.state[currentKey];
        const currentIndex = (startFrom + 1) % products.length;

        this.updateProductsState(currentKey, currentIndex);
    };

    prevProductActivate = (currentKey) => () => {
        const { products } = this.props;
        const startFrom = this.state[currentKey];
        const currentIndex = (startFrom - 1 + products.length) % products.length;

        this.updateProductsState(currentKey, currentIndex);
    };

    updateProductsState = (currentKey, currentIndex) => {
        this.setState({
            ...this.state,
            [currentKey]: currentIndex,
        });
    };

    handleProductRemove = (event: CustomEvent<ProductsApi.Product>) => {
        const { onRemove } = this.props;

        if (onRemove) {
            const { repositoryId } = event.detail;
            if (event.detail.bundle) {
                const { quantity, repositoryId: bundleId } = event.detail.bundle;
                onRemove(`${repositoryId}:(${quantity})${bundleId}`);
            } else {
                onRemove(repositoryId);
            }
        }

        const { left, right } = this.state;
        const { products } = this.props;

        // Handle the case when the last (and only) left product is being deleted
        if (left === -1) {
            return this.setState({
                left: 0,
            });
        }

        // When the 3rd product is being deleted, left-product is set to (index 0), right-product is set to (index 1)
        // (In order not to show the same products when only 2 of them are left)
        if (products.length === 3) {
            return this.setState({
                left: 0,
                right: 1,
            });
        }

        // Get max index from state to compare it with products length
        const valueWithMaxIndex = Math.max(left, right);

        // Update the state if products length equals to the max index from state
        // They will be the same when the last product is being deleted

        // Cover the case when both last-left and last-right products are being deleted at the same time
        const isStateEqual = left === right;

        if (isStateEqual && right + 1 === products.length) {
            return this.setState({
                left: left - 1,
                right: right - 1,
            });
        }

        // Cover the case when either last-left or last-right products is being deleted
        if (products.length === valueWithMaxIndex + 1) {
            const updatedKey = this.getKeyByValue(this.state, valueWithMaxIndex);
            this.updateProductsState([updatedKey], this.state[updatedKey] - 1);
        }

        return null;
    };

    onProductBuyOnlineClick = (event: CustomEvent) => {
        const { productsUIAAS } = this.props;
        const ccProductToShow = productsUIAAS.find(
            (product) => product.commercialReference === event.detail.repositoryId,
        );

        this.setState({ ccProductIdToShow: ccProductToShow.repositoryId });
    };

    handleCcDialogClosed = () => {
        this.setState({ ccProductIdToShow: null });
    };

    handleCcBtnBuyOnlineAvailability = (event: CustomEvent<Record<string, boolean>>) => {
        this.setState({ ccBuyOnlineAvailability: event.detail });
    };

    handleCcBroadcastPrices = (event: CustomEvent<{
        prices: Record<string, string>;
        priceFromLabel?: string;
    }>) => {
        this.setState({ ccPricesMap: event.detail?.prices });
    };

    getPartnerPrice = (product: ProductsApi.Product): string => {
        if (this.state.ccPricesMap && product.repositoryId in this.state.ccPricesMap) {
            return this.state?.ccPricesMap[product.repositoryId];
        }
    };

    getAdditionalDistributors = (products: MappedProduct[]): ProductsDistributorsInfo => {
        const { productsUIAASinfo, labels } = this.props;

        if (productsUIAASinfo.length === 0) return;
        const additionalDistributors = {};
        products.forEach((product, index) => {
            if(productsUIAASinfo[index].apcURL) {
                const { apcURL } = productsUIAASinfo[index];
                additionalDistributors[product.productId] =
                    [ getAPCDistributor(apcURL, labels.purchaseOnAPCLabel), ];
            }
        })
        return additionalDistributors;
    }

    renderProductsColumn = (isMinified) => {
        const {
            productsRatings,
            bazaarVoiceEnabled,
            contextRoot,
            countryCode,
            languageCode,
            products,
            productsUIAAS,
            productsUIAASinfo,
            onUpdateMyProductsList,
            labels,
            commerceConnector,
            myProducts,
            isAPCSite,
            apcCta,
            productAvailabilityInfo,
            downloadButtonLabels,
        } = this.props;
        const { left, right } = this.state;
        const additionalDistributors = this.getAdditionalDistributors(products);
        const leftProduct = products[left];
        const rightProduct = products[right];
        const leftProductUIAAS = productsUIAAS[left];
        const rightProductUIAAS = productsUIAAS[right];
        const productsForCC = productsUIAAS
          .filter(({ circularType }) => {
            if (!circularType) {
              return true;
            }

            return circularType === CircularProductTypes.LINEAR;
          })
          .map(
            ({ commercialReference, description, pictureUrl: imgUrl, pictureAltText: imgAlt }) => ({
                commercialReference,
                description,
                imgUrl,
                imgAlt,
            }),
        );

        const HOST = process.env.API_HOST || `${window.location.protocol}//${window.location.host}`;
        const HOST_WITH_LOCALE = process.env.API_HOST
            ?
                isHostnameContainsCC()
                    ? `${process.env.API_HOST}${contextRoot}/${languageCode}`
                    : `${process.env.API_HOST}${contextRoot}/${countryCode}/${languageCode}`

            :
                isHostnameContainsCC()
                    ? `${HOST}${contextRoot}/${languageCode}`
                    : `${HOST}${contextRoot}/${countryCode}/${languageCode}`;

        return (
            <React.Fragment>
                <PesCcMain
                    host={HOST_WITH_LOCALE}
                    products={productsForCC}
                    productIdToShow={this.state.ccProductIdToShow}
                    onCcDialogClosed={this.handleCcDialogClosed}
                    onCcBtnBuyOnlineAvailability={this.handleCcBtnBuyOnlineAvailability}
                    onCcBroadcastPrices={this.handleCcBroadcastPrices}
                    additionalDistributors={!this.props.isAPCSite && additionalDistributors}
                />
                <MobileProductsColumn
                    bazaarVoiceEnabled={bazaarVoiceEnabled}
                    productsRatings={productsRatings}
                    isAPCSite={isAPCSite}
                    apcCta={apcCta}
                    productAvailabilityInfo={productAvailabilityInfo}
                    contextRoot={contextRoot}
                    countryCode={countryCode}
                    languageCode={languageCode}
                    product={leftProduct}
                    multipleProducts={!(products.length === 1)}
                    productUIAAS={leftProductUIAAS}
                    productsUIAASinfo={productsUIAASinfo}
                    labels={labels}
                    downloadButtonLabels={downloadButtonLabels}
                    ccBuyOnlineAvailability={this.state.ccBuyOnlineAvailability}
                    partnerPrice={this.getPartnerPrice(leftProductUIAAS)}
                    productsLength={products.length}
                    onRemove={this.handleProductRemove}
                    commerceConnector={commerceConnector}
                    myProducts={myProducts}
                    onUpdateMyProductsList={onUpdateMyProductsList}
                    onProductBuyOnlineClick={this.onProductBuyOnlineClick}
                    startFrom={left}
                    isMinified={isMinified}
                    onNextProductChange={this.nextProductActivate('left')}
                    onPrevProductChange={this.prevProductActivate('left')}
                    additionalDistributors={additionalDistributors}
                />
                {products.length > 1 ? (
                    <MobileProductsColumn
                        bazaarVoiceEnabled={bazaarVoiceEnabled}
                        productsRatings={productsRatings}
                        isAPCSite={isAPCSite}
                        apcCta={apcCta}
                        productAvailabilityInfo={productAvailabilityInfo}
                        contextRoot={contextRoot}
                        countryCode={countryCode}
                        languageCode={languageCode}
                        product={rightProduct}
                        multipleProducts={true}
                        productUIAAS={rightProductUIAAS}
                        productsUIAASinfo={productsUIAASinfo}
                        labels={labels}
                        downloadButtonLabels={downloadButtonLabels}
                        ccBuyOnlineAvailability={this.state.ccBuyOnlineAvailability}
                        partnerPrice={this.getPartnerPrice(rightProductUIAAS)}
                        productsLength={products.length}
                        onRemove={this.handleProductRemove}
                        commerceConnector={commerceConnector}
                        myProducts={myProducts}
                        onUpdateMyProductsList={onUpdateMyProductsList}
                        onProductBuyOnlineClick={this.onProductBuyOnlineClick}
                        startFrom={right}
                        isMinified={isMinified}
                        onNextProductChange={this.nextProductActivate('right')}
                        onPrevProductChange={this.prevProductActivate('right')}
                        additionalDistributors={additionalDistributors}
                    />
                ) : null}
            </React.Fragment>
        );
    };

    render() {
        const {
            products,
            categories,
            keyCategories,
            toggleSectionHandler,
            toggleSectionState,
            showDevPrioritiesInfo,
            highlightDifferencesChange,
            highlightDifferences,
            handleHideSimilarChange,
            hideSimilarFeatures,
            runtimeInfo,
            linkClickHandlerCallback
        } = this.props;

        const { left, right } = this.state;
        const leftProduct = products[left];
        const rightProduct = products[right];

        const mobileProductsClasses = classNames({
            [styles.mobileProducts]: true,
            container: true,
        });

        const minifiedProductClasses = classNames({
            [styles.productCardsContainer]: true,
            [styles.stickyProducts]: true,
            [styles.stuckProducts]: this.state.isStuck
        });

        const checkCategoriesOfProducts = (products: MappedProduct[]) => {
            return products.filter(product => !isEmpty(product.productCategories)).length
        };
        const categoryList = [keyCategories, categories];
        return (
            <div className={mobileProductsClasses}>
                {products.map(product => this.preloadImage(product))}
                {products.length > 1 && (checkCategoriesOfProducts(products) > 0) && <OptionsCard
                    products={products}
                    categoryList={categoryList}
                    highlightDifferencesChange={highlightDifferencesChange}
                    highlightDifferences={highlightDifferences}
                    handleHideSimilarChange={handleHideSimilarChange}
                    hideSimilarFeatures={hideSimilarFeatures}
                />}
                <div className={styles.productCardsContainer}>
                    {this.renderProductsColumn(false)}
                </div>
                <div ref={this.stickyContainer} className={styles.stickyContainer}>
                    <div ref={this.minifiedProductsContainer} className={minifiedProductClasses}>
                        {this.renderProductsColumn(true)}
                    </div>
                    <div>
                        {keyCategories.length > 0
                            ? keyCategories.map((keyCategory) => {
                                const currentStateKey = `${keyCategory.priority}_${keyCategory.name}`;

                                return isCategorySectionAvailable(keyCategory, products) ? (
                                    <MobileCompareResults
                                        key={keyCategory.name}
                                        category={keyCategory}
                                        currentStateKey={currentStateKey}
                                        toggleSectionHandler={toggleSectionHandler}
                                        toggleState={toggleSectionState[currentStateKey]}
                                        showDevPrioritiesInfo={showDevPrioritiesInfo}
                                        products={products}
                                        leftProduct={leftProduct}
                                        rightProduct={products.length > 1 ? rightProduct : {}}
                                        hideSimilarFeatures={hideSimilarFeatures}
                                        highlightDifferences={highlightDifferences}
                                        runtimeInfo={runtimeInfo}
                                    />
                                ) : null;
                            })
                            : null}
                        {categories.map((category) => {
                            const currentStateKey = `${category.priority}_${category.name}`;

                            return isCategorySectionAvailable(category, products) ? (
                                <MobileCompareResults
                                    key={category.name}
                                    category={category}
                                    currentStateKey={currentStateKey}
                                    toggleSectionHandler={toggleSectionHandler}
                                    toggleState={toggleSectionState[currentStateKey]}
                                    showDevPrioritiesInfo={showDevPrioritiesInfo}
                                    products={products}
                                    leftProduct={leftProduct}
                                    rightProduct={products.length > 1 ? rightProduct : {}}
                                    hideSimilarFeatures={hideSimilarFeatures}
                                    highlightDifferences={highlightDifferences}
                                    runtimeInfo={runtimeInfo}
                                    linkClickHandlerCallback={linkClickHandlerCallback}
                                />
                            ) : null;
                        })}
                    </div>
                </div>
            </div>
        );
    }
}

export default MobileProducts;
